import { LIMITS, legalNoticesOf, type AgentDraft, type LegalNotice } from "@nemo/agent-schema";
import { useI18n } from "../i18n.js";
import type { FieldErrors } from "../draft.js";
import { Field } from "./ui.js";

type Notice = { kind: "text" | "url"; value: string };

/**
 * The notices shown after the greeting: up to five, each text (500 characters) or a link, in the order the visitor sees them.
 * Drafts that still have the older single `legalNotice` show it as notice 1; the first change saves the list and drops the old field.
 */
export function LegalNoticesEditor({ form, update, errors }: { form: AgentDraft; update: (fn: (f: AgentDraft) => AgentDraft) => void; errors: FieldErrors }) {
  const { t } = useI18n();
  const notices: Notice[] = legalNoticesOf(form as { legalNotice?: LegalNotice; legalNotices?: LegalNotice[] });
  const save = (next: Notice[]) =>
    update((f) => {
      const { legalNotice: _old, ...rest } = f as AgentDraft & { legalNotice?: unknown };
      return { ...rest, legalNotices: next } as AgentDraft;
    });
  const change = (i: number, patch: Partial<Notice>) => save(notices.map((n, k) => (k === i ? { ...n, ...patch } : n)));
  const move = (i: number, by: -1 | 1) => {
    const next = notices.slice();
    const [item] = next.splice(i, 1);
    next.splice(i + by, 0, item as Notice);
    save(next);
  };

  return (
    <fieldset className="legal-notices">
      <legend>{t("legal.title")}</legend>
      <p className="help">{t("legal.help", { max: LIMITS.legalNoticesMax })}</p>
      {notices.length === 0 && <p className="hint">{t("legal.none")}</p>}
      <ol className="legal-list">
        {notices.map((n, i) => {
          const id = `legal-${i}`;
          const err = errors[`legalNotices.${i}.value`] ?? errors[`legalNotice.value`];
          return (
            <li key={i} className="legal-item">
              <Field label={t("legal.n", { n: i + 1 })} htmlFor={id} error={err} count={n.kind === "text" ? { value: n.value.length, max: LIMITS.legalMax } : undefined}>
                <div className="tabs" role="tablist" aria-label={t("legal.type", { n: i + 1 })}>
                  {(["text", "url"] as const).map((k) => (
                    <button key={k} type="button" role="tab" aria-selected={n.kind === k} className={n.kind === k ? "active" : ""} onClick={() => k !== n.kind && change(i, { kind: k, value: "" })}>
                      {k === "text" ? t("legal.text") : t("legal.link")}
                    </button>
                  ))}
                </div>
                {n.kind === "text" ? (
                  <textarea id={id} rows={3} value={n.value} placeholder={t("legal.placeholderText")} onChange={(e) => change(i, { value: e.target.value })} />
                ) : (
                  <input id={id} type="url" value={n.value} placeholder="https://example.com/privacy" onChange={(e) => change(i, { value: e.target.value })} />
                )}
              </Field>
              <div className="row">
                <button type="button" className="btn secondary" disabled={i === 0} aria-label={t("legal.up", { n: i + 1 })} onClick={() => move(i, -1)}>↑</button>
                <button type="button" className="btn secondary" disabled={i === notices.length - 1} aria-label={t("legal.down", { n: i + 1 })} onClick={() => move(i, 1)}>↓</button>
                <button type="button" className="btn secondary" aria-label={t("legal.remove", { n: i + 1 })} onClick={() => save(notices.filter((_, k) => k !== i))}>{t("common.remove")}</button>
              </div>
            </li>
          );
        })}
      </ol>
      <button type="button" className="btn secondary" disabled={notices.length >= LIMITS.legalNoticesMax} onClick={() => save([...notices, { kind: "text", value: "" }])}>
        {t("legal.add")}
      </button>
    </fieldset>
  );
}
